import {
  Box,
  Button,
  Grid,
  LinearProgress,
  Paper,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CheckRounded from '@material-ui/icons/CheckRounded';
import EmojiEventsOutlined from '@material-ui/icons/EmojiEventsOutlined';
import HelpOutline from '@material-ui/icons/HelpOutline';
import LockOutlined from '@material-ui/icons/LockOutlined';
import MonetizationOnOutlined from '@material-ui/icons/MonetizationOnOutlined';
import StarBorderRounded from '@material-ui/icons/StarBorderRounded';
import WhatshotRounded from '@material-ui/icons/WhatshotRounded';
import CarouselSkeleton from 'common/components/skeleton/carouselSkeleton';
import {
  BASE_S3,
  COMPANY_COLORS,
  GENERAL_TEXT_COLOR,
  HEADER_TEXT_COLOR,
} from 'config/consts';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { rawContentView, setLoading } from '../activitiesActions';
import { abrirLinkDeAtividade } from '../abrirLinkDeAtividade';
import { criarFormatador } from './ganho';
import { getJourneyRanking, getJourneys } from './journeyActions';
import JourneyActivityModal from './journeyActivityModal';
import JourneyRanking from './journeyRanking';

const generalColor = COMPANY_COLORS.general.general_color;
const SECTION_TITLE_COLOR = HEADER_TEXT_COLOR || GENERAL_TEXT_COLOR;
const STAT_COLOR = '#000';
const CURRENT_COLOR = '#E08A1E';
const LOCKED_COLOR = '#B9B9B9';
const BOX_HEIGHT = 108;
const STEP_WIDTH = 168;

const useStyles = makeStyles({
  progressRoot: {
    height: 8,
    borderRadius: 4,
    backgroundColor: '#EAEAEA',
  },
  progressBar: {
    borderRadius: 4,
    backgroundColor: generalColor,
  },
  track: {
    display: 'flex',
    alignItems: 'flex-start',
    overflowX: 'auto',
    paddingBottom: 8,
  },
});

const STATUS = { DONE: 'done', CURRENT: 'current', LOCKED: 'locked' };

function buildLevelNumbers(actions) {
  const parents = new Map();
  actions.forEach((action) => {
    const level = action.prerequisites?.action_level_id;
    if (level) {
      parents.set(level.id, level.journey_level_id);
    }
  });

  const depthOf = (levelId, seen) => {
    const parentId = parents.get(levelId);
    if (!parentId || seen.has(levelId)) {
      return 1;
    }
    seen.add(levelId);
    return depthOf(parentId, seen) + 1;
  };

  const numbers = new Map();
  parents.forEach((parentId, levelId) => {
    numbers.set(levelId, depthOf(levelId, new Set()));
  });
  return numbers;
}

function buildSteps(actions) {
  const levelNumbers = buildLevelNumbers(actions);

  const steps = actions.map((action, index) => {
    const levelId = action.prerequisites?.action_level_id?.id;
    const level = levelNumbers.get(levelId) || index + 1;
    const locked =
      !!action.prerequisites?.action_has_prerequisites &&
      !action.prerequisites?.user_done_prerequisites;

    return {
      action,
      level,
      status: action.hasDone
        ? STATUS.DONE
        : locked
        ? STATUS.LOCKED
        : STATUS.CURRENT,
    };
  });

  steps.sort((a, b) => a.level - b.level);

  const currentLevel = steps.find((step) => step.status !== STATUS.DONE)?.level;

  return steps.map((step) => ({
    ...step,
    distance: currentLevel ? step.level - currentLevel : 0,
  }));
}

function stepOpacity(step) {
  if (step.status !== STATUS.LOCKED) {
    return 1;
  }
  return Math.max(0.35, 1 - 0.22 * step.distance);
}

function stepSubtitle(step, formatarGanho) {
  const { action, status, distance, level } = step;
  if (status === STATUS.DONE) {
    return action.points > 0
      ? `Concluído · +${formatarGanho(parseFloat(action.points))}`
      : 'Concluído';
  }
  if (status === STATUS.CURRENT) {
    return action.click_me_label || 'Clique para participar';
  }
  return distance <= 1 ? 'Desbloqueia após a atual' : `Desbloqueia no nível ${level}`;
}

function Journeys(props) {
  const {
    auth,
    journey,
    website,
    history,
    getJourneys,
    getJourneyRanking,
    setLoading,
  } = props;
  const classes = useStyles();
  const formatarGanho = criarFormatador(website?.stationData);
  const [rankingJourney, setRankingJourney] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null);

  useEffect(() => {
    getJourneys();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const id = history?.location?.state?.abrirRanking;
    if (!id) {
      return;
    }
    const alvo = (journey.list || []).find(
      (item) => String(item.id) === String(id)
    );
    if (!alvo) {
      return;
    }
    openRanking(alvo);
    history.replace({ ...history.location, state: {} });
    // eslint-disable-next-line
  }, [journey.list]);

  function openActivity(action) {
    setSelectedAction(null);
    if (action.type_id === 13) {
      abrirLinkDeAtividade(action, props.rawContentView);
      return;
    }
    setLoading(true);
    history.push({
      pathname: `/homepage/atividades/${action.id}`,
      state: { fromJourney: true },
    });
  }

  function openRanking(item) {
    getJourneyRanking(item.id);
    setRankingJourney(item);
  }

  function renderHeader(item, steps) {
    const done = steps.filter((step) => step.status === STATUS.DONE);
    const streak = item.streak || 0;
    const points = done.reduce(
      (total, step) => total + (parseFloat(step.action.points) || 0),
      0
    );

    return (
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        style={{ gap: 16 }}
      >
        <Box>
          <Typography
            variant="h5"
            style={{ fontWeight: 'bold', color: GENERAL_TEXT_COLOR }}
          >
            {item.name}
          </Typography>
          <Typography variant="body2" style={{ color: '#8A8A8A' }}>
            {item.description || 'Complete as etapas e desbloqueie recompensas'}
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" style={{ gap: 16 }}>
          <Tooltip
            title={
              streak > 0
                ? `Você avançou nesta jornada por ${streak} ${
                    streak === 1 ? 'dia seguido' : 'dias seguidos'
                  }. Participe de uma atividade hoje para manter a sequência.`
                : 'Sequência de dias seguidos com progresso na jornada. Participe de uma atividade para começar a sua.'
            }
          >
            <Box display="flex" alignItems="center" style={{ cursor: 'help' }}>
              <WhatshotRounded
                style={{ color: CURRENT_COLOR, fontSize: 20, marginRight: 4 }}
              />
              <Typography
                variant="body2"
                style={{ fontWeight: 'bold', color: CURRENT_COLOR }}
              >
                {streak}
              </Typography>
            </Box>
          </Tooltip>
          <Tooltip title="Ganho acumulado nas atividades concluídas desta jornada">
            <Box display="flex" alignItems="center" style={{ cursor: 'help' }}>
              <MonetizationOnOutlined
                style={{ color: STAT_COLOR, fontSize: 20, marginRight: 4 }}
              />
              <Typography
                variant="body2"
                style={{ fontWeight: 'bold', color: STAT_COLOR }}
              >
                {formatarGanho(points)}
              </Typography>
            </Box>
          </Tooltip>

          <Box display="flex" alignItems="center">
            <StarBorderRounded
              style={{
                color: SECTION_TITLE_COLOR,
                fontSize: 20,
                marginRight: 4,
              }}
            />
            <Typography
              variant="body2"
              style={{ fontWeight: 'bold', color: SECTION_TITLE_COLOR }}
            >
              {done.length} de {steps.length}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<EmojiEventsOutlined />}
            onClick={() => openRanking(item)}
            style={{
              color: CURRENT_COLOR,
              borderColor: CURRENT_COLOR,
              borderRadius: 20,
              textTransform: 'none',
              fontWeight: 'bold',
            }}
          >
            Ranking
          </Button>
        </Box>
      </Box>
    );
  }

  function renderProgress(steps) {
    const done = steps.filter((step) => step.status === STATUS.DONE).length;
    const percentage =
      steps.length > 0 ? Math.round((done / steps.length) * 100) : 0;

    return (
      <LinearProgress
        variant="determinate"
        value={percentage}
        classes={{ root: classes.progressRoot, bar: classes.progressBar }}
      />
    );
  }

  function renderStepBox(step) {
    const { action, status } = step;
    const isDone = status === STATUS.DONE;
    const isCurrent = status === STATUS.CURRENT;
    const accent = isDone ? generalColor : isCurrent ? CURRENT_COLOR : LOCKED_COLOR;

    return (
      <Box
        data-testid="journey-step"
        data-status={status}
        onClick={status === STATUS.LOCKED ? undefined : () => setSelectedAction(action)}
        style={{
          position: 'relative',
          height: BOX_HEIGHT,
          borderRadius: 12,
          overflow: 'hidden',
          cursor: status === STATUS.LOCKED ? 'default' : 'pointer',
          background: isDone
            ? `${generalColor}1F`
            : isCurrent
            ? `${CURRENT_COLOR}1F`
            : '#F2F2F2',
          border: isCurrent
            ? `2px solid ${CURRENT_COLOR}`
            : isDone
            ? `2px solid ${generalColor}33`
            : '2px dashed #D8D8D8',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {status !== STATUS.LOCKED && action.image ? (
          <img
            src={`${BASE_S3}/${action.image}`}
            alt={action.name}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        ) : status === STATUS.LOCKED ? (
          <LockOutlined style={{ color: LOCKED_COLOR, fontSize: 32 }} />
        ) : (
          <HelpOutline style={{ color: accent, fontSize: 32 }} />
        )}

        {isDone ? (
          <Box
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: generalColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CheckRounded style={{ color: '#fff', fontSize: 16 }} />
          </Box>
        ) : null}

        {isCurrent ? (
          <Box
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              padding: '2px 10px',
              borderRadius: 12,
              background: CURRENT_COLOR,
            }}
          >
            <Typography
              variant="caption"
              style={{ color: '#fff', fontWeight: 'bold' }}
            >
              agora
            </Typography>
          </Box>
        ) : null}
      </Box>
    );
  }

  function renderStepLabel(step) {
    const { action, status, level } = step;
    const isLocked = status === STATUS.LOCKED;

    return (
      <Box style={{ textAlign: 'center', marginTop: 12 }}>
        <Typography
          variant="body2"
          title={action.name}
          style={{
            fontWeight: 'bold',
            color: isLocked ? '#8A8A8A' : undefined,
            minHeight: 40,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {action.name}
        </Typography>
        <Typography
          variant="caption"
          style={{ color: '#9A9A9A', display: 'block', minHeight: 20 }}
        >
          {stepSubtitle(step, formatarGanho)}
        </Typography>
        <Box display="flex" justifyContent="center" style={{ marginTop: 6 }}>
          <Box
            style={{
              padding: '2px 10px',
              borderRadius: 12,
              background:
                status === STATUS.DONE
                  ? `${generalColor}26`
                  : status === STATUS.CURRENT
                  ? `${CURRENT_COLOR}26`
                  : '#EDEDED',
            }}
          >
            <Typography
              variant="caption"
              style={{
                color:
                  status === STATUS.DONE
                    ? generalColor
                    : status === STATUS.CURRENT
                    ? CURRENT_COLOR
                    : '#8A8A8A',
              }}
            >
              Nível {level}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  function renderTrack(steps) {
    return (
      <Box className={classes.track} style={{ marginTop: 24 }}>
        {steps.map((step, index) => (
          <React.Fragment key={step.action.id}>
            {index > 0 ? (
              <Box
                style={{
                  flex: '1 1 24px',
                  minWidth: 24,
                  height: 3,
                  marginTop: BOX_HEIGHT / 2,
                  borderRadius: 2,
                  background:
                    steps[index - 1].status === STATUS.DONE
                      ? generalColor
                      : '#E4E4E4',
                }}
              />
            ) : null}
            <Box
              style={{
                width: STEP_WIDTH,
                minWidth: STEP_WIDTH,
                opacity: stepOpacity(step),
              }}
            >
              {renderStepBox(step)}
              {renderStepLabel(step)}
            </Box>
          </React.Fragment>
        ))}
      </Box>
    );
  }

  if (journey.loading) {
    return (
      <Grid
        container
        direction="column"
        spacing={2}
        style={{ minHeight: '100vh' }}
      >
        {Array.from(new Array(3)).map((el, index) => (
          <Grid item container xs={12} key={index}>
            <CarouselSkeleton elementsHeight={150} />
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <div
      className="container h-100"
      style={{ paddingBottom: '30%', padding: 0, minHeight: '100vh' }}
    >
      <Typography
        variant="h4"
        style={{ marginBottom: 24, color: SECTION_TITLE_COLOR }}
      >
        Jornadas
      </Typography>

      {journey.error ? (
        <Box>
          <Typography variant="body1">
            Não foi possível carregar suas jornadas.
          </Typography>
          <Button
            variant="outlined"
            onClick={() => getJourneys()}
            style={{
              marginTop: 12,
              textTransform: 'none',
              color: generalColor,
              borderColor: generalColor,
            }}
          >
            Tentar de novo
          </Button>
        </Box>
      ) : journey.list.length < 1 ? (
        <Typography variant="body1">
          Você ainda não está participando de nenhuma jornada.
        </Typography>
      ) : (
        journey.list.map((item) => {
          const steps = buildSteps(item.actions || []);
          return (
            <Paper
              key={item.id}
              elevation={0}
              style={{
                padding: 24,
                marginBottom: 32,
                borderRadius: 16,
                border: '1px solid #ECECEC',
              }}
            >
              {renderHeader(item, steps)}
              <Box style={{ marginTop: 20 }}>{renderProgress(steps)}</Box>
              {renderTrack(steps)}
            </Paper>
          );
        })
      )}

      <JourneyActivityModal
        action={selectedAction}
        onClose={() => setSelectedAction(null)}
        onConfirm={openActivity}
        formatarGanho={formatarGanho}
      />

      <JourneyRanking
        visible={!!rankingJourney}
        onClose={() => setRankingJourney(null)}
        loading={journey.rankingLoading}
        ranking={journey.ranking}
        userId={auth.user.id}
        journeyName={rankingJourney?.name}
        error={journey.rankingError}
        onRetry={() => rankingJourney && getJourneyRanking(rankingJourney.id)}
        formatarGanho={formatarGanho}
        nomeDaMoeda={website?.stationData?.currency_name}
      />

    </div>
  );
}

Journeys.propTypes = {
  auth: PropTypes.object.isRequired,
  journey: PropTypes.object.isRequired,
  website: PropTypes.object,
  history: PropTypes.object.isRequired,
  getJourneys: PropTypes.func.isRequired,
  getJourneyRanking: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
  return {
    auth: state.auth,
    journey: state.journey,
    website: state.website,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    { getJourneys, getJourneyRanking, rawContentView, setLoading },
    dispatch
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(Journeys);
